/**
 * Utility functions for Automation components
 *
 * Note: All dates/times are displayed in the user's local timezone.
 * The backend returns UTC timestamps, and cloudApi.toLocalDate() converts them
 * to local Date objects. All formatting functions use toLocaleString() and
 * related methods to ensure proper local timezone display.
 */

/**
 * Format a date/time for display in local timezone
 * Dates from cloudApi are already converted to local time
 */
export const formatLastRun = (date) => {
  if (!date) return "Never run";

  const now = new Date();
  const runDate = new Date(date);
  const diff = now - runDate;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 30) {
    const month = runDate.toLocaleDateString("en-US", { month: "short" });
    const day = runDate.getDate();
    const year = runDate.getFullYear();
    const time = runDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (year !== now.getFullYear()) {
      return `${day} ${month} ${year}, ${time}`;
    }
    return `${day} ${month}, ${time}`;
  }

  if (days > 0) {
    const month = runDate.toLocaleDateString("en-US", { month: "short" });
    const day = runDate.getDate();
    const time = runDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `${day} ${month} ${time}`;
  }

  if (hours > 0) {
    return runDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  if (minutes < 1) return "Just now";
  return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
};

/**
 * Get the next run time for a scheduled automation
 */
export const getNextRunTime = (schedule) => {
  if (!schedule || !schedule.enabled) return null;

  const now = new Date();

  // Prefer the server-computed next run (correct for arbitrary cron schedules,
  // e.g. "every Monday at 09:00"). Fall back to lastRun + interval only for
  // legacy preset schedules where nextRun is not yet populated.
  let nextRun;
  if (schedule.nextRun) {
    nextRun = new Date(schedule.nextRun);
  } else if (schedule.interval) {
    const intervalMs = schedule.interval * 60 * 1000;
    const lastRun = schedule.lastRun ? new Date(schedule.lastRun) : now;
    nextRun = new Date(lastRun.getTime() + intervalMs);
  } else {
    return null;
  }

  const diff = nextRun - now;
  if (diff <= 0) return "Running soon";

  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Format schedule interval for display
 */
export const formatSchedule = (schedule) => {
  if (!schedule || !schedule.enabled) return "Scheduling OFF";

  // Presets map to a known interval — keep the compact "Every 12h" / "Every 10m"
  // form. Custom schedules (a cron with no matching preset interval) fall back
  // to the descriptive cron label.
  const interval = schedule.interval;
  if (interval > 0) {
    if (interval < 60) {
      return `Every ${interval}m`;
    } else if (interval < 1440) {
      const hours = Math.floor(interval / 60);
      return `Every ${hours}h`;
    }
    const days = Math.floor(interval / 1440);
    return `Every ${days}d`;
  }

  if (schedule.cronExpression) {
    return describeCron(schedule.cronExpression);
  }

  // Enabled but no interval and no cron — shouldn't happen, but never render
  // "Every undefinedm".
  return "Scheduling OFF";
};

// ============================================================================
// Cron building & description (used by the custom schedule builder)
// ============================================================================

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Build a 5-field cron expression from guided builder inputs.
 *
 * @param {object} opts
 * @param {"daily"|"weekly"|"monthly"} opts.repeat
 * @param {number[]} [opts.daysOfWeek] - 0=Sun..6=Sat, for weekly
 * @param {number} [opts.dayOfMonth] - 1..28, for monthly
 * @param {string} opts.time - "HH:MM" (24h, local)
 * @returns {string} cron expression, e.g. "0 9 * * 1,2"
 */
export const buildCron = ({ repeat, daysOfWeek = [], dayOfMonth = 1, time = "09:00" }) => {
  const [hStr, mStr] = String(time).split(":");
  const minute = Math.max(0, Math.min(59, parseInt(mStr, 10) || 0));
  const hour = Math.max(0, Math.min(23, parseInt(hStr, 10) || 0));

  if (repeat === "weekly") {
    const dows = [...daysOfWeek].sort((a, b) => a - b);
    const dowField = dows.length > 0 ? dows.join(",") : "*";
    return `${minute} ${hour} * * ${dowField}`;
  }

  if (repeat === "monthly") {
    const dom = Math.max(1, Math.min(28, parseInt(dayOfMonth, 10) || 1));
    return `${minute} ${hour} ${dom} * *`;
  }

  // daily
  return `${minute} ${hour} * * *`;
};

const formatCronTime = (minute, hour) => {
  const m = String(Math.max(0, Math.min(59, parseInt(minute, 10) || 0))).padStart(2, "0");
  const h = String(Math.max(0, Math.min(23, parseInt(hour, 10) || 0))).padStart(2, "0");
  return `${h}:${m}`;
};

const expandDowField = (field) => {
  // Supports lists ("1,3"), ranges ("1-5"), single values, and "*".
  if (field === "*") return null;
  const days = new Set();
  for (const part of field.split(",")) {
    if (part.includes("-")) {
      const [a, b] = part.split("-").map((n) => parseInt(n, 10));
      for (let d = a; d <= b; d++) days.add(((d % 7) + 7) % 7);
    } else {
      const d = parseInt(part, 10);
      if (!Number.isNaN(d)) days.add(((d % 7) + 7) % 7);
    }
  }
  return [...days].sort((a, b) => a - b);
};

/**
 * Produce a human-readable label for a cron expression.
 * Handles the guided-builder shapes (daily / weekly / monthly at a fixed time),
 * common presets, and falls back to the raw expression for anything exotic.
 *
 * @param {string} cron - 5-field cron expression
 * @returns {string}
 */
export const describeCron = (cron) => {
  if (!cron || typeof cron !== "string") return "";

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [min, hr, dom, mon, dow] = parts;

  // Sub-daily presets: "*/N * * * *" or "0 */N * * *"
  if (mon === "*" && dom === "*" && dow === "*") {
    const everyMin = min.match(/^\*\/(\d+)$/);
    if (everyMin && hr === "*") return `Every ${everyMin[1]} minutes`;
    const everyHr = hr.match(/^\*\/(\d+)$/);
    if (everyHr && /^\d+$/.test(min)) return `Every ${everyHr[1]} hours`;
    if (hr === "*" && /^\d+$/.test(min)) return "Every hour";
  }

  const hasFixedTime = /^\d+$/.test(min) && /^\d+$/.test(hr);
  const atTime = hasFixedTime ? ` at ${formatCronTime(min, hr)}` : "";

  // Weekly: day-of-week constrained, day-of-month unconstrained
  if (dom === "*" && mon === "*" && dow !== "*") {
    const days = expandDowField(dow);
    if (days && days.length > 0) {
      if (days.length === 7) return `Daily${atTime}`;
      const isWeekdays = days.length === 5 && days.every((d) => d >= 1 && d <= 5);
      if (isWeekdays) return `Weekdays${atTime}`;
      const labels = days.map((d) => DOW_LABELS[d]).join(", ");
      return `Every ${labels}${atTime}`;
    }
  }

  // Monthly: day-of-month constrained
  if (/^\d+$/.test(dom) && mon === "*" && dow === "*") {
    return `Monthly on day ${parseInt(dom, 10)}${atTime}`;
  }

  // Every N days: "0 0 */N * *"
  const everyNDays = dom.match(/^\*\/(\d+)$/);
  if (everyNDays && mon === "*" && dow === "*") {
    const n = parseInt(everyNDays[1], 10);
    return n === 1 ? `Daily${atTime}` : `Every ${n} days${atTime}`;
  }

  // Plain daily: "m h * * *"
  if (dom === "*" && mon === "*" && dow === "*" && hasFixedTime) {
    return `Daily${atTime}`;
  }

  return cron;
};

